import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Settings, Zap, ShieldCheck, Play, Tv, Wifi, Search, Layout, Key } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Inserir Dados de Acesso IPTV", href: "/blog/como-inserir-dados-acesso-iptv-v2" }
  ];

  const faq = [
    {
      question: "Qual o formato de login mais seguro?",
      answer: "O formato API (Xtream Codes) é o mais seguro e eficiente. Ele evita que você tenha que lidar com links longos e permite que o servidor atualize sua grade de canais automaticamente."
    },
    {
      question: "O sistema diferencia maiúsculas de minúsculas?",
      answer: "Sim, absolutamente. Se sua senha tem uma letra 'A' maiúscula e você digitar 'a' minúscula, o login falhará. Recomendamos copiar e colar os dados sempre que possível."
    },
    {
      question: "Posso mudar minha senha depois de configurar?",
      answer: "Sim, você pode solicitar a alteração da sua senha à nossa equipe de suporte. Após a mudança, você precisará atualizar os dados no seu aplicativo de IPTV."
    }
  ];

  const benefits = [
    {
      title: "Configuração à Prova de Erros",
      description: "Aprenda a identificar cada campo de preenchimento e evite as falhas mais comuns de autenticação.",
      icon: <Key className="w-6 h-6" />
    },
    {
      title: "Zapping Instantâneo",
      description: "Inserir os dados corretamente via API garante que a troca de canais aconteça em milissegundos.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Sincronização Automática",
      description: "Uma vez logado via API, suas categorias de filmes e séries estarão sempre atualizadas sem esforço.",
      icon: <RefreshCw className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Recebimento dos Dados",
      description: "Nossa equipe envia seu Usuário, Senha e URL do Servidor via WhatsApp."
    },
    {
      title: "Escolha do Método",
      description: "Dentro do app, selecione 'Login with Xtream Codes API'."
    },
    {
      title: "Preenchimento Rigoroso",
      description: "Insira os dados respeitando cada caractere, ponto e barra da URL."
    },
    {
      title: "Validação Final",
      description: "Clique em 'Add User' ou 'Login' e aguarde a mensagem de sucesso."
    }
  ];

  const diferenciais = [
    {
      title: "Protocolos Modernos",
      description: "Trabalhamos apenas com métodos de login que protegem sua privacidade e identidade digital.",
      icon: <ShieldCheck className="w-6 h-6" />
    },
    {
      title: "Suporte à Cópia e Cola",
      description: "Nossos links são gerados para serem facilmente copiados, evitando erros de digitação manual.",
      icon: <Info className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. A Importância da Precisão nos Dados de Acesso",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Pode parecer uma tarefa simples, mas <strong>como inserir dados de acesso IPTV</strong> de forma correta é a base de toda a sua experiência de entretenimento. 90% dos chamados de suporte na <strong>P2 PRO</strong> são resolvidos apenas corrigindo um erro de digitação na URL do servidor ou uma letra trocada na senha. No mundo do streaming de elite, a precisão cirúrgica é o que garante que você não perca nem um minuto da sua programação favorita.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia técnico, vamos dissecar o processo de autenticação. Vamos explicar o que é cada campo (Username, Password, Server URL) e por que o método Xtream Codes se tornou o padrão ouro da indústria. Ao final desta leitura, você saberá exatamente como configurar qualquer aplicativo de IPTV, em qualquer dispositivo, com a confiança de um técnico especializado.
          </p>
        </div>
      )
    },
    {
      title: "2. Os 3 Campos Fundamentais: O Que Inserir em Cada Um",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Key className="w-6 h-6 text-electric" /> Usuário e Senha</h4>
              <p className="text-sm text-muted-foreground">Suas credenciais únicas. Dica: evite espaços extras no início ou no fim dos nomes ao copiar do WhatsApp.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-2 flex items-center gap-2"><Tv className="w-6 h-6 text-amber-500" /> URL do Servidor (Host)</h4>
              <p className="text-sm text-muted-foreground">O endereço 'HTTP' que aponta para os datacenters da P2 PRO. Deve ser digitado exatamente como: http://servidor.com:8080</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Passo a Passo Técnico: O Caminho da Autenticação",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Siga este roteiro para evitar o erro de 'Invalid Details' no seu aplicativo:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Selecione o Método API</h4>
              <p className="text-muted-foreground">No menu inicial do app, procure por 'Xtream Codes' ou 'Login with API'. Nunca use o método M3U se você tiver os dados de API, pois ele é mais lento.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">!</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Cuidado com o Corretor Automático</h4>
              <p className="text-sm text-muted-foreground">Ao digitar no celular ou Smart TV, o corretor pode mudar letras da senha ou adicionar pontos. Desative o corretor durante o preenchimento dos dados.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Especialista para Manter o Acesso Ativo",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Mantenha sua assinatura sempre funcionando com estas diretrizes simples:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Salve no Bloco de Notas</h5>
                <p className="text-sm text-muted-foreground">Mantenha uma cópia dos seus dados no celular. Se precisar formatar sua TV, você terá os dados à mão para reconfigurar em segundos.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Não compartilhe sua Senha</h5>
                <p className="text-sm text-muted-foreground">Nosso sistema monitora acessos simultâneos. Compartilhar os dados pode causar o bloqueio automático da sua conta por segurança.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Suporte via QR Code</h5>
                <p className="text-sm text-muted-foreground">Muitos aplicativos P2 PRO geram um código na tela. Basta escanear com seu celular para que nossa equipe insira os dados para você remotamente.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias Dispositivos"
        title="Como Inserir Dados de Acesso IPTV: Passo a Passo Sem Erros"
        subtitle="O guia definitivo para autenticar seu aplicativo de IPTV com precisão e rapidez. Evite falhas de login agora."
        heroImage="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Inserir os dados de acesso é o momento mais importante da sua configuração IPTV. É neste ponto que seu dispositivo 'conversa' com os nossos servidores de elite para liberar milhares de canais e filmes. Na P2 PRO, prezamos pela facilidade, mas sabemos que um simples erro de digitação pode ser a diferença entre um lazer perfeito e um suporte técnico demorado.",
          "Neste guia denso, vamos te ensinar a arte do preenchimento correto. Vamos explicar o que é cada campo, as armadilhas comuns dos teclados de Smart TVs e como garantir que seu login via API seja feito de primeira, garantindo a melhor performance e organização de categorias que nosso sistema oferece.",
          "Prepare-se para uma aula prática de configuração. No final desta leitura, você será capaz de configurar qualquer player de IPTV em menos de 2 minutos, com a certeza de que está extraindo o máximo potencial da tecnologia P2 PRO."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🔑 Pronto para Ativar seu Acesso de Elite?"
        ctaDescription="Ainda não tem seus dados? Solicite agora mesmo um teste gratuito e receba seu login no WhatsApp em segundos."
        ctaButtonText="Receber Meus Dados Agora"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-inserir-dados-acesso-iptv-v2")({
  head: () => ({
    meta: [
      { title: "Como Inserir Dados de Acesso IPTV: Guia Técnico 2026" },
      { name: "description", content: "Aprenda a inserir corretamente seu usuário, senha e URL no aplicativo de IPTV. Guia para evitar erros de login e garantir a sincronização da lista." },
    ],
  }),
  component: ArticlePage,
});

import { RefreshCw } from "lucide-react";
